import React from "react";

/**
 * Skill Icons Component
 *
 * Icons used in this component are from the Skill Icons project:
 * - Repository: https://github.com/tandpfun/skill-icons
 * - License: MIT License
 * - Copyright (c) 2022 tandpfun
 */

// Import all skill icons as URLs
import TypeScriptIconUrl from "/src/assets/skill-icons/skill-icons--typescript.svg";
import CSharpIconUrl from "/src/assets/skill-icons/skill-icons--cs.svg";
import JavaIconUrl from "/src/assets/skill-icons/skill-icons--java-dark.svg";
import PythonIconUrl from "/src/assets/skill-icons/skill-icons--python-dark.svg";
import HTMLIconUrl from "/src/assets/skill-icons/skill-icons--html.svg";
import CSSIconUrl from "/src/assets/skill-icons/skill-icons--css.svg";
import SCSSIconUrl from "/src/assets/skill-icons/skill-icons--sass.svg";
import JavaScriptIconUrl from "/src/assets/skill-icons/skill-icons--javascript.svg";
import ReactIconUrl from "/src/assets/skill-icons/skill-icons--react-dark.svg";
import DotNetIconUrl from "/src/assets/skill-icons/skill-icons--dotnet.svg";
import ThreeJSIconUrl from "/src/assets/skill-icons/skill-icons--threejs-dark.svg";
import DjangoIconUrl from "/src/assets/skill-icons/skill-icons--django.svg";
import MongoDBIconUrl from "/src/assets/skill-icons/skill-icons--mongodb.svg";
import Neo4jIconUrl from "/src/assets/skill-icons/vscode-icons--file-type-neo4j.svg";
import AzureIconUrl from "/src/assets/skill-icons/skill-icons--azure-dark.svg";
import AWSIconUrl from "/src/assets/skill-icons/skill-icons--aws-dark.svg";
import GCPIconUrl from "/src/assets/skill-icons/skill-icons--gcp-dark.svg";
import DockerIconUrl from "/src/assets/skill-icons/skill-icons--docker.svg";
import KubernetesIconUrl from "/src/assets/skill-icons/skill-icons--kubernetes.svg";
import OpenShiftIconUrl from "/src/assets/skill-icons/skill-icons--openshift.svg";
import JenkinsIconUrl from "/src/assets/skill-icons/skill-icons--jenkins-dark.svg";
import GithubActionsIconUrl from "/src/assets/skill-icons/skill-icons--githubactions-light.svg";
import GitIconUrl from "/src/assets/skill-icons/skill-icons--git.svg";
import VimIconUrl from "/src/assets/skill-icons/devicon--vim.svg";
import LinuxIconUrl from "/src/assets/skill-icons/skill-icons--linux-light.svg";
import PostmanIconUrl from "/src/assets/skill-icons/skill-icons--postman.svg";
import JiraIconUrl from "/src/assets/skill-icons/logos--jira.svg";
import AngularIconUrl from "/src/assets/skill-icons/skill-icons--angular-dark.svg";
import SQLIconUrl from "/src/assets/skill-icons/skill-icons--mysql-dark.svg";
import AstroIconUrl from "/src/assets/skill-icons/skill-icons--astro.svg";
import ObsidianIconUrl from "/src/assets/skill-icons/skill-icons--obsidian-dark.svg";
import ConfluenceIconUrl from "/src/assets/skill-icons/skill-icons--confluence.svg";
import DocusaurusIconUrl from "/src/assets/skill-icons/skill-icons--docusaurus.svg";
import MarkdownIconUrl from "/src/assets/skill-icons/skill-icons--markdown-dark.svg";

interface SkillIconProps {
  skillName: string;
  size?: number;
  className?: string;
}

// Map skill names to their corresponding icon URLs
const skillIconMap: Record<string, string> = {
  // Programming Languages
  TypeScript: TypeScriptIconUrl,
  "C#": CSharpIconUrl,
  Java: JavaIconUrl,
  Python: PythonIconUrl,
  HTML: HTMLIconUrl,
  CSS: CSSIconUrl,
  Sass: SCSSIconUrl,
  JavaScript: JavaScriptIconUrl,

  // Frameworks
  Angular: AngularIconUrl,
  ".NET": DotNetIconUrl,
  React: ReactIconUrl,
  "Three.js": ThreeJSIconUrl,
  "React Three Fiber": ReactIconUrl,
  Django: DjangoIconUrl,
  Astro: AstroIconUrl,
  Docusaurus: DocusaurusIconUrl,

  // Databases
  SQL: SQLIconUrl,
  MongoDB: MongoDBIconUrl,

  // Cloud Technologies
  Azure: AzureIconUrl,
  "Amazon Web Services (AWS)": AWSIconUrl,
  "Google Cloud Platform (GCP)": GCPIconUrl,

  // CI/CD
  Docker: DockerIconUrl,
  Kubernetes: KubernetesIconUrl,
  OpenShift: OpenShiftIconUrl,
  Jenkins: JenkinsIconUrl,
  "GitHub Actions": GithubActionsIconUrl,

  // Tools
  Git: GitIconUrl,
  Vim: VimIconUrl,
  Linux: LinuxIconUrl,
  Postman: PostmanIconUrl,
  Jira: JiraIconUrl,
  Markdown: MarkdownIconUrl,
  Obsidian: ObsidianIconUrl,
  Confluence: ConfluenceIconUrl,
};

const SkillIcon: React.FC<SkillIconProps> = ({
  skillName,
  size = 16,
  className = "",
}) => {
  const iconUrl = skillIconMap[skillName];

  if (!iconUrl) {
    return null; // Return null if no icon is found
  }

  return (
    <img
      src={iconUrl}
      alt={`${skillName} icon`}
      className={className}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        flexShrink: 0,
      }}
    />
  );
};

export default SkillIcon;
